//import { HttpClient             } from '@angular/common/http';
import { Injectable             } from "@angular/core";
import { BehaviorSubject        } from 'rxjs/BehaviorSubject';
import { empty                  } from 'rxjs/observable/empty';
import { Observable             } from "rxjs/Observable";
import { TimeTrackerBaseApi     } from './generic.service';
import { IPlanningListResponse, IActivityDuringPlanningResponse  } from "../../typings/api";
import { IPlanning, IPlanningUser, IActivityPlanning } from "../../typings/timetracker/timetracker";
import { zip } from 'rxjs/observable/zip';

@Injectable()
export class PlanningService {

    private list$ = new BehaviorSubject<IPlanningListResponse>(undefined);
    private planningDetails$ = new BehaviorSubject<IPlanning>(undefined);
    private planningUsers$ = new BehaviorSubject<IPlanningUser[]>(undefined);
    private planningActivities$ = new BehaviorSubject<IActivityDuringPlanningResponse[]>(undefined);

    /**
     * Return all plannings
     */
    selectPlanningFetchAll(): Observable<IPlanningListResponse> {
        return this.list$.asObservable();
    }

    /**
     * Retrieve the planning details
    */
    selectPlanningFetch(): Observable<IPlanning> {
        return this.planningDetails$.asObservable();
    }

    /**
     * Retrieve the user scheduled for a planning
     */
    selectPlanningUsersFetch(): Observable<IPlanningUser[]> {
        return this.planningUsers$.asObservable();
    }

    /**
     * Retrieve the user activity planning
    */
    selectPlanningActivities(): Observable<IActivityDuringPlanningResponse[]> {
        return this.planningActivities$.asObservable();
    }

    /**
     * @param {number} planningId The planning to retrieve the details from
     */
    dispatchPlanningFetch( planningId: number ) {
      zip(
          this.$api.genericGetCall( `/admin/plannings/${planningId}` ),
          this.$api.genericGetCall( `/admin/plannings/${planningId}/users` ),
          this.$api.genericGetCall( `/admin/plannings/${planningId}/actual-activities` )
      ).subscribe( ( [ planningResponse, userResponse, activitiesResponse ] ) => {
        this.planningDetails$.next( planningResponse as IPlanning );
        this.planningUsers$.next( userResponse as IPlanningUser[] );
        this.planningActivities$.next( activitiesResponse as IActivityDuringPlanningResponse[] );
      } );
    }

    /**
     * @param {number}          planningId  The planning to update
     * @param {Array<number>}   users       The user Id's to add
     */
    dispatchRemoveUserFromPlanning( planningId: number, userId: number ) {
        console.log( `[USERS] Remove user to planning. Planning: ${planningId}, user: ${userId}` );
        this.$api.genericDeleteCall( `/admin/plannings/${planningId}/users/${userId}`).subscribe( () => {
            console.log( `[USERS] Planning: ${planningId}, removed user: ${userId}` );
        } );
    }

    /**
     * @param {number}          planningId  The planning to update
     * @param {Array<number>}   users       The user Id's to add
     */
    dispatchAddUsersToPlanning( planningId: number, users: Array<IPlanningUser> ) {
        console.log( '[USERS] Add user to planning' );
        return new Promise( ( resolve, reject ) => {
            this.$api.genericPostCall( `/admin/plannings/${planningId}/users`, users
            ).catch( ( error ) => {
                reject( error );
                return empty();
            } ).subscribe( ( planning: IPlanning ) => {
                resolve( planning );
                this.dispatchPlanningFetch( planningId );
            } );
        } );
    }

    /**
     * @param {number} year       The year of the planning
     * @param {number} isoWeek    The ISO week number of the planning
     * @param {number} isoWeekDay The ISO week day of the planning
     * @param {string} startTime  The String datetime representation, only the time part is used
     */
    dispatchCreatePlanning( year: number, isoWeek: number, isoWeekDay: number, startTime: string ) {
        console.log( '[USERS] Create a new planning' );
        return new Promise( ( resolve, reject ) => {
            this.$api.genericPostCall( '/admin/plannings', {
                "year":         year,
                "iso_week":     isoWeek,
                "iso_weekday":  isoWeekDay,
                "start_time":   startTime
            } ).catch( ( error ) => {
                reject( error );
                return empty();
            }  ).subscribe( ( planning: IPlanning ) => {
                resolve( planning );
                this.dispatchPlanningFetch( planning.id );
            } );
        } )
    }

    /**
     * @param {number} id         Planning ID
     * @param {number} year       The year of the planning
     * @param {number} isoWeek    The ISO week number of the planning
     * @param {number} isoWeekDay The ISO week day of the planning
     * @param {string} startTime  The String datetime representation, only the time part is used
     */
    dispatchUpdatePlanning( id: number, year: number, isoWeek: number, isoWeekDay: number, startTime: string ) {
        console.log( '[USERS] Updated planning ' + id );
        return new Promise( ( resolve, reject ) => {
            this.$api.genericPatchCall( `/admin/plannings/${id}`, {
                "id":           id,
                "year":         year,
                "iso_week":     isoWeek,
                "iso_weekday":  isoWeekDay,
                "start_time":   startTime
            } ).catch( ( error ) => {
                reject( error );
                return empty();
            }  ).subscribe( ( planning: IPlanning ) => {
                resolve( planning );
                this.dispatchPlanningFetch( planning.id );
            } );
        } )
    }

    /**
     * @param {number} id                                  Planning ID
     * @param {IActivityDuringPlanningResponse} activity   The activity to save
     */
    dispatchInsertActivityInPlanning( activity: IActivityPlanning ) {
      console.log( '[PLANNING] Updated activity in planning ', activity );
      return new Promise( ( resolve, reject ) => {
          this.$api.genericPostCall( `/admin/plannings/${activity.planning_id}/activities`, [ activity ] ).catch( ( error ) => {
              reject( error );
              return empty();
          }  ).subscribe( () => {
              resolve();
              this.dispatchPlanningFetch( activity.planning_id );
          } );
      } )
    }
    /**
     * @param {number} id                                  Planning ID
     * @param {IActivityDuringPlanningResponse} activity   The activity to save
     */
    dispatchUpdateActivityInPlanning( activity: IActivityPlanning ) {
      console.log( '[PLANNING] Updated activity in planning ', activity );
      return new Promise( ( resolve, reject ) => {
          this.$api.genericPutCall( `/admin/plannings/${activity.planning_id}/activities/${activity.activity_id}`, activity ).catch( ( error ) => {
              reject( error );
              return empty();
            }  ).subscribe( () => {
              resolve();
              this.dispatchPlanningFetch( activity.planning_id );
          } );
      } )
  }

    /**
     * Retrieve a list of all users
     */
    dispatchPlanningFetchAll( limit: number, skip: number, sortField = 'start_time', sortDirection?: string ): void {
        console.log( '[PLANNING] Retrieve all planning' );
        this.$api.genericListCall( '/admin/plannings', limit, skip, sortField, sortDirection ).subscribe( ( response: IPlanningListResponse ) => {
            this.list$.next( response );
        } );
    }

    dispatchGetActivitiesDuringPlanning( planningId: number ) {
      console.log( '[PLANNING] Retrieves all activities that have hours booked within the period of a planning and combine this with the activities already in the planning' );
      this.$api.genericGetCall( `/admin/plannings/${planningId}/actual-activities` ).subscribe( ( response: IActivityDuringPlanningResponse[] ) => {
        this.planningActivities$.next( response );
    } );
    }

    constructor( private $api: TimeTrackerBaseApi ) {}
}



// WEBPACK FOOTER //
// ./src/providers/planning.service.ts